import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../config';

export interface TransactionalEmailTemplate {
  id: string;
  type: string; // e.g., 'receipt', 'reservation_confirmation'
  name: string;
  subject: string;
  body: string;
  isDefault: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface TransactionalEmailSettings {
  defaultSenderName: string;
  defaultSenderEmail: string;
  defaultFooter: string;
  brandingLogoUrl?: string;
  smtpHost?: string;
  smtpPort?: number;
  security?: string;
  provider?: string;
  updatedAt: number;
}

const getTemplatesCollection = (locationId: string) => collection(db, `locations/${locationId}/transactionalEmailTemplates`);
const getSettingsDocRef = (locationId: string) => doc(db, `locations/${locationId}/transactionalEmailSettings/settings`);

export const getTemplates = async (locationId: string): Promise<TransactionalEmailTemplate[]> => {
  try {
    const snapshot = await getDocs(getTemplatesCollection(locationId));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TransactionalEmailTemplate));
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

export const createTemplate = async (locationId: string, template: Omit<TransactionalEmailTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
  try {
    const now = Date.now();
    const docRef = doc(getTemplatesCollection(locationId));
    const templateData = {
      ...template,
      id: docRef.id,
      isDefault: !!template.isDefault,
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(docRef, templateData);
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

export const updateTemplate = async (locationId: string, id: string, updates: Partial<TransactionalEmailTemplate>): Promise<void> => {
  try {
    console.log('updateTemplate called with:', { locationId, id });
    console.log('Updates object:', JSON.stringify(updates, null, 2));
    
    const docRef = doc(getTemplatesCollection(locationId), id);
    const updateData = {
      ...updates,
      updatedAt: Date.now(),
    };
    
    console.log('Final update data being sent to Firestore:', JSON.stringify(updateData, null, 2));
    
    // Get the document before update to compare
    const beforeDoc = await getDoc(docRef);
    console.log('Document before update:', beforeDoc.exists() ? JSON.stringify(beforeDoc.data(), null, 2) : 'Document does not exist');
    
    await updateDoc(docRef, updateData);
    
    // Verify the update worked by getting the document again
    const afterDoc = await getDoc(docRef);
    console.log('Document after update:', afterDoc.exists() ? JSON.stringify(afterDoc.data(), null, 2) : 'Document does not exist');
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

export const deleteTemplate = async (locationId: string, id: string): Promise<void> => {
  const docRef = doc(getTemplatesCollection(locationId), id);
  await deleteDoc(docRef);
};

export const setDefaultTemplate = async (locationId: string, type: string, id: string): Promise<void> => {
  // Unset previous default
  const q = query(getTemplatesCollection(locationId), where('type', '==', type), where('isDefault', '==', true));
  const snapshot = await getDocs(q);
  for (const docSnap of snapshot.docs) {
    await updateDoc(doc(getTemplatesCollection(locationId), docSnap.id), { isDefault: false });
  }
  // Set new default
  await updateDoc(doc(getTemplatesCollection(locationId), id), { isDefault: true, updatedAt: Date.now() });
};

export const getTransactionalEmailSettings = async (locationId: string): Promise<TransactionalEmailSettings | null> => {
  const docRef = getSettingsDocRef(locationId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? (docSnap.data() as TransactionalEmailSettings) : null;
};

export const updateTransactionalEmailSettings = async (locationId: string, settings: Partial<TransactionalEmailSettings>): Promise<void> => {
  const docRef = getSettingsDocRef(locationId);
  await setDoc(docRef, {
    ...settings,
    updatedAt: Date.now(),
  }, { merge: true });
}; 